import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { MapPin, X, ChevronRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SafeImage } from "@/components/ui/safe-image";
import { cn } from "@/lib/utils";
import { useProximityCheckIn, NearbyCafe } from "@/hooks/useProximityCheckIn";
import { useIncompleteVibe } from "@/components/IncompleteVibePromptProvider";
import { useBottomPromptSlot } from "@/components/BottomPromptSlot";
import { supabase } from "@/integrations/supabase/client";
import { getCafeDisplayImageUrl } from "@/lib/cafeImages";
import { CafePhotoPlaceholder } from "@/components/CafePhotoPlaceholder";

function CafePhotoImage({ cafe }: { cafe: NearbyCafe }) {
  const { data: communityPhoto } = useQuery({
    queryKey: ["cafe-first-photo", cafe.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("cafe_photos")
        .select("photo_url")
        .eq("cafe_id", cafe.id)
        .eq("is_approved", true)
        .order("is_cover", { ascending: false })
        .order("quality_score", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data?.photo_url ?? null;
    },
    enabled: !cafe.cover_image_url,
    staleTime: 60000,
  });

  const imageUrl = communityPhoto || getCafeDisplayImageUrl(cafe);

  if (imageUrl) {
    return (
      <SafeImage
        src={imageUrl}
        alt={cafe.name}
        className="w-full h-full object-cover"
      />
    );
  }

  // Was a generic map pin, which said nothing about which cafe this is. The
  // initial tile is the same fallback every other card now uses.
  return <CafePhotoPlaceholder name={cafe.name} />;
}

export function ProximityCheckInPrompt() {
  const { nearbyCafes, isFirstCheckIn, dismiss } = useProximityCheckIn();
  const navigate = useNavigate();
  const { isActive: isVibeFlowActive, recentlyCompletedCafeId } = useIncompleteVibe();
  const { register, unregister, isVisible } = useBottomPromptSlot();

  const filteredCafes = nearbyCafes.filter((c) => c.id !== recentlyCompletedCafeId);
  const shouldShow = filteredCafes.length > 0 && !isVibeFlowActive;
  const rendered = shouldShow && isVisible("proximity");

  useEffect(() => {
    if (shouldShow) {
      register("proximity");
      return () => unregister("proximity");
    }
  }, [shouldShow, register, unregister]);

  // Analytics only: one event per distinct prompt actually shown to the user.
  const shownForCafeRef = useRef<string | null>(null);
  const primaryCafeId = filteredCafes[0]?.id ?? null;
  useEffect(() => {
    if (!rendered || !primaryCafeId) return;
    if (shownForCafeRef.current === primaryCafeId) return;
    shownForCafeRef.current = primaryCafeId;
    try {
      posthog.capture("checkin_prompt_shown", {
        source: "proximity",
        cafe_id: primaryCafeId,
      });
    } catch { /* ignore */ }
  }, [rendered, primaryCafeId]);

  if (!rendered) return null;

  const handleCheckInClick = (cafe: NearbyCafe) => {
    dismiss();
    navigate(`/cafe/${cafe.slug}?action=checkin`);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismiss();
  };

  // Single cafe — original compact prompt
  if (filteredCafes.length === 1) {
    const cafe = filteredCafes[0];
    return (
      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50",
          "p-4 pb-[calc(1rem+env(safe-area-inset-bottom)+4rem)] lg:pb-[calc(1rem+env(safe-area-inset-bottom))]",
          "animate-in slide-in-from-bottom-4 duration-300"
        )}
      >
        <div
          className={cn(
            "relative mx-auto max-w-md",
            "rounded-xl bg-[#F3EDE6] border-2 border-primary shadow-xl",
            "overflow-hidden"
          )}
        >
          <button
            onClick={handleDismiss}
            className={cn(
              "absolute top-3 right-3 z-10",
              "p-1.5 rounded-full",
              "bg-background/80 backdrop-blur-sm",
              "text-muted-foreground/60 hover:text-muted-foreground",
              "transition-colors"
            )}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-stretch">
            <div className="relative w-24 h-24 flex-shrink-0">
              <CafePhotoImage cafe={cafe} />
            </div>
            <div className="flex-1 p-3 pr-10 flex flex-col justify-center">
              <div className="flex items-center gap-1.5 text-primary text-xs font-medium mb-0.5">
                {isFirstCheckIn ? (
                  <>
                    <Sparkles className="h-3 w-3 text-foreground" />
                    <span className="text-foreground">Looks like you've got good taste.</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-3 w-3" />
                    <span>You're here!</span>
                  </>
                )}
              </div>
              {isFirstCheckIn ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  You're at {cafe.name} — check in and let others know how it's working today.
                </p>
              ) : (
                <>
                  <p className="font-semibold text-foreground text-sm leading-tight line-clamp-1">
                    {cafe.name}
                  </p>
                  {cafe.neighborhood && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {cafe.neighborhood}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 pt-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="flex-1 text-muted-foreground"
            >
              {isFirstCheckIn ? "I just got here" : "Maybe Later"}
            </Button>
            <Button
              size="sm"
              onClick={() => handleCheckInClick(cafe)}
              className={cn("flex-1 gap-1", isFirstCheckIn && "bg-[#4F6F5D] hover:bg-[#456352] text-white")}
            >
              Check In
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Multiple cafes — chooser prompt
  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "p-4 pb-[calc(1rem+env(safe-area-inset-bottom)+4rem)] lg:pb-[calc(1rem+env(safe-area-inset-bottom))]",
        "animate-in slide-in-from-bottom-4 duration-300"
      )}
    >
      <div
        className={cn(
          "relative mx-auto max-w-md",
          "rounded-xl bg-[#F3EDE6] border-2 border-primary shadow-xl",
          "overflow-hidden"
        )}
      >
        <button
          onClick={handleDismiss}
          className={cn(
            "absolute top-3 right-3 z-10",
            "p-1.5 rounded-full",
            "bg-background/80 backdrop-blur-sm",
            "text-muted-foreground/60 hover:text-muted-foreground",
            "transition-colors"
          )}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-3 pb-1">
          <div className="flex items-center gap-1.5 text-primary text-xs font-medium mb-1">
            <MapPin className="h-3 w-3" />
            <span>You're near multiple spots!</span>
          </div>
          <p className="text-sm text-muted-foreground">Where are you checking in?</p>
        </div>

        <div className="px-3 py-2 space-y-1.5">
          {filteredCafes.map((cafe) => (
            <button
              key={cafe.id}
              onClick={() => handleCheckInClick(cafe)}
              className={cn(
                "w-full flex items-center gap-3 p-2 rounded-lg",
                "bg-background/60 hover:bg-background transition-colors",
                "text-left"
              )}
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                <CafePhotoImage cafe={cafe} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm leading-tight line-clamp-1">
                  {cafe.name}
                </p>
                {cafe.neighborhood && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {cafe.neighborhood}
                  </p>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>

        <div className="px-3 pb-3 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="w-full text-muted-foreground"
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  );
}
